<template>
  <div class="root">
    <LoadingSpinner v-if="isLoading" size="medium" variant="inline" />

    <p v-else-if="loadFailed" class="load-error" role="alert">{{ $t("eventContest.error.fetchFailed") }}</p>

    <div v-else-if="contest" class="contents">
      <article class="body">
        <img v-if="imageUrl" class="hero" :src="imageUrl" :alt="title" />
        <div class="body-text">
          <h1 class="title">{{ title }}</h1>
          <div v-if="contentHtml" class="description" v-html="contentHtml" />
        </div>
      </article>

      <aside class="summary">
        <p class="summary-title">{{ title }}</p>
        <p class="deadline-label">{{ $t("eventContestV2Detail.deadlineLabel") }}</p>
        <p class="deadline">{{ deadline }}</p>
        <button type="button" class="apply-button" :disabled="!canApply" @click="onClickApply">
          {{ $t("eventContestV2Detail.applyButton") }}
        </button>
      </aside>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { format } from "date-fns";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { NolaPfContestClient } from "@/lib/clients";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import { getDateFnsLocale } from "@/lib/utils/i18n/locale";
import { optimizeImage } from "@/lib/utils/url";

const nolaPfContestClient = new NolaPfContestClient();

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export default Vue.extend<Data, Methods, Computed, Props>({
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    LoadingSpinner,
  },
  props: {
    id: String,
  },
  data() {
    return {
      isLoading: true,
      loadFailed: false,
      contest: null,
    };
  },
  async created() {
    if (!this.id) {
      this.loadFailed = true;
      this.isLoading = false;
      return;
    }

    try {
      this.contest = await nolaPfContestClient.fetchMicroCmsContest(this.id);
    } catch {
      this.loadFailed = true;
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    title() {
      return this.contest?.contest ?? "";
    },
    imageUrl() {
      const url = this.contest?.image?.url;
      if (!url) return "";
      return optimizeImage(url);
    },
    contentHtml() {
      const content = this.contest?.content ?? "";
      if (!content) return "";
      const source = HTML_TAG_PATTERN.test(content) ? content : content.replace(/\n/g, "<br>");
      return this.$sanitize(source);
    },
    deadline() {
      const deadline = this.contest?.deadline;
      if (!deadline) return "-";

      const locale = getDateFnsLocale(this.$i18n.locale);
      const formatStr = this.$t("common.deadlineFormat") as string;
      return format(deadline, formatStr, { locale });
    },
    canApply() {
      return this.contest?.contestStatus === "accepting";
    },
  },
  methods: {
    onClickApply() {
      if (!this.canApply) return;

      this.$router.push({
        name: "eventContestTerms",
        params: { id: this.id },
      });
    },
  },
});

interface Props {
  id: string;
}

interface Data {
  isLoading: boolean;
  loadFailed: boolean;
  contest: NolaPfMicroCmsContest | null;
}

interface Computed {
  title: string;
  imageUrl: string;
  contentHtml: string;
  deadline: string;
  canApply: boolean;
}

interface Methods {
  onClickApply(): void;
}
</script>

<style lang="scss" scoped>
.root {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: calc(100vh - #{$header-height});
  overflow: hidden;
  background-color: var(--color-background-normal);
  padding: 40px 16px;

  @include mq("sp") {
    height: 100%;
    min-height: 0;
    padding: 0;
  }
}

.load-error {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.contents {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  min-height: 0;

  @include mq("sp") {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }
}

.body {
  flex: 1 1 740px;
  min-width: 0;
  background-color: var(--color-surface-base);

  @include mq("pc") {
    align-self: stretch;
    min-height: 0;
    overflow-y: auto;
  }

  @include mq("sp") {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
  }
}

.hero {
  display: block;
  width: 100%;
  height: auto;
}

.body-text {
  box-sizing: border-box;
  padding: 40px;

  @include mq("sp") {
    padding: 24px 16px;
  }
}

.title {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  word-break: break-word;
}

.description {
  margin-top: 24px;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  word-break: break-word;

  ::v-deep p {
    margin: 0 0 24px;
  }

  ::v-deep p:last-child {
    margin-bottom: 0;
  }

  ::v-deep pre {
    white-space: pre-wrap;
  }
}

.summary {
  box-sizing: border-box;
  flex: 0 0 236px;
  width: 236px;
  padding: 24px;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-md);

  @include mq("pc") {
    align-self: flex-start;
    max-height: calc(100vh - #{$header-height} - 80px);
    overflow-y: auto;
  }

  @include mq("sp") {
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    width: 100%;
    padding: 24px 16px;
    background-color: var(--color-background-normal);
    border-radius: 0;
    box-shadow: var(--shadow-lg);
  }
}

.summary-title {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  word-break: break-word;

  @include mq("sp") {
    display: none;
  }
}

.deadline-label {
  margin: 24px 0 0;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);

  @include mq("sp") {
    margin-top: 0;
  }
}

.deadline {
  margin: 4px 0 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.apply-button {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  margin-top: 24px;
  padding: 8px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }
}
</style>
